import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from "date-fns";
import { es } from "date-fns/locale";
import {
  Calendar as CalendarIcon,
  Clock,
  Users,
  CheckCircle,
  XCircle,
  Trash2,
  RefreshCw,
  Droplet,
  Monitor,
  Tv,
  Armchair,
  Square,
  Mic,
  MoreHorizontal,
  Loader2,
  Inbox,
  Download,
  FileText,
  ChevronLeft,
  ChevronRight,
  X,
  BarChart3,
  Package,
  Ban,
  Plus
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RESOURCE_ICONS = { water: Droplet, projector: Monitor, tv: Tv, chairs: Armchair, tables: Square, microphone: Mic, kitchen: Square, other: MoreHorizontal };
const RESOURCE_LABELS = { water: "Botellas de agua", projector: "Proyector", tv: "TV", chairs: "Sillas", tables: "Mesas", microphone: "Micrófono", kitchen: "Cocina", other: "Otras" };

const StatusBadge = ({ status }) => {
  const cfg = { pending: { cls: "status-pending", label: "Pendiente" }, approved: { cls: "status-approved", label: "Aprobado" }, rejected: { cls: "status-rejected", label: "Rechazado" } };
  const c = cfg[status] || cfg.pending;
  return <span className={c.cls} data-testid={`status-badge-${status}`}>{c.label}</span>;
};

const StatCard = ({ label, value, color }) => (
  <div className="admin-stat">
    <p className="label-text mb-2">{label}</p>
    <p className={`text-3xl font-bold ${color}`}>{value}</p>
  </div>
);

// Calendar View
const CalendarView = ({ calendarData, onDateClick }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDay = monthStart.getDay();
  const adjustedStartDay = startDay === 0 ? 6 : startDay - 1;

  const getEventsForDate = (date) => calendarData[format(date, "yyyy-MM-dd")] || [];
  const getStatusColor = (status) => status === 'approved' ? 'bg-green-500' : status === 'rejected' ? 'bg-red-500' : 'bg-yellow-500';

  return (
    <div className="bg-white border border-[#E5E5E5]" data-testid="calendar-view">
      <div className="flex items-center justify-between p-4 border-b border-[#E5E5E5]">
        <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 hover:bg-[#F5F5F5]"><ChevronLeft className="w-5 h-5" /></button>
        <h3 className="heading-h3 capitalize">{format(currentMonth, "MMMM yyyy", { locale: es })}</h3>
        <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 hover:bg-[#F5F5F5]"><ChevronRight className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-7 border-b border-[#E5E5E5]">
        {['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'].map(d => (
          <div key={d} className="p-2 text-center text-xs font-semibold uppercase tracking-wide text-[#666] border-r border-[#E5E5E5] last:border-r-0">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {Array.from({ length: adjustedStartDay }).map((_, i) => <div key={`e-${i}`} className="min-h-[90px] border-r border-b border-[#E5E5E5] bg-[#FAFAFA]" />)}
        {days.map(day => {
          const events = getEventsForDate(day);
          const isToday = isSameDay(day, new Date());
          return (
            <div key={day.toISOString()} className={`min-h-[90px] border-r border-b border-[#E5E5E5] p-2 cursor-pointer hover:bg-[#F5F5F5] ${isToday ? 'bg-orange-50' : ''}`} onClick={() => events.length > 0 && onDateClick(day, events)}>
              <div className={`text-sm font-semibold mb-1 ${isToday ? 'text-[#EA580C]' : ''}`}>{format(day, "d")}</div>
              {events.slice(0, 2).map((ev, i) => (
                <div key={i} className={`text-[10px] px-1 py-0.5 text-white truncate mb-0.5 ${getStatusColor(ev.status)}`}>{ev.time_start || ev.time} {ev.organizer?.split(' ')[0]}</div>
              ))}
              {events.length > 2 && <div className="text-[10px] text-[#666]">+{events.length - 2}</div>}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 p-3 border-t border-[#E5E5E5]">
        <div className="flex items-center gap-2"><div className="w-3 h-3 bg-yellow-500"></div><span className="text-xs text-[#666]">Pendiente</span></div>
        <div className="flex items-center gap-2"><div className="w-3 h-3 bg-green-500"></div><span className="text-xs text-[#666]">Aprobada</span></div>
        <div className="flex items-center gap-2"><div className="w-3 h-3 bg-red-500"></div><span className="text-xs text-[#666]">Rechazada</span></div>
      </div>
    </div>
  );
};

const DayDetailModal = ({ isOpen, onClose, date, events }) => {
  if (!isOpen || !date) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white w-full max-w-lg max-h-[80vh] overflow-auto m-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#E5E5E5]">
          <h3 className="heading-h3">{format(date, "EEEE d 'de' MMMM", { locale: es })}</h3>
          <button onClick={onClose} className="p-2 hover:bg-[#F5F5F5]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          {events.map((ev, i) => (
            <div key={i} className="border border-[#E5E5E5] p-3">
              <div className="flex items-center justify-between mb-2"><span className="font-semibold">{ev.time_start || ev.time} - {ev.time_end || ''} hrs</span><StatusBadge status={ev.status} /></div>
              <p className="text-sm">{ev.organizer}</p>
              {ev.event_name && <p className="text-xs text-[#EA580C] font-medium">{ev.event_name}</p>}
              <p className="text-xs text-[#666] flex items-center gap-1"><Users className="w-3 h-3" /> {ev.people} personas</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PIE_COLORS = ['#F59E0B', '#10B981', '#EF4444'];

const AdminPage = ({ token, onLogout }) => {
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [calendarData, setCalendarData] = useState({});
  const [chartData, setChartData] = useState({ by_status: [], by_month: [], by_resource: [], total_people: 0 });
  const [supplyRequests, setSupplyRequests] = useState([]);
  const [supplyStats, setSupplyStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, materials: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [supplyFilter, setSupplyFilter] = useState("all");

  // Admin blocks state
  const [adminBlocks, setAdminBlocks] = useState([]);
  const [blockDate, setBlockDate] = useState(null);
  const [blockTimeStart, setBlockTimeStart] = useState("");
  const [blockTimeEnd, setBlockTimeEnd] = useState("");
  const [blockSalon, setBlockSalon] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [blockFullDay, setBlockFullDay] = useState(false);

  // Dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reservationToDelete, setReservationToDelete] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusAction, setStatusAction] = useState(null);
  const [observation, setObservation] = useState("");
  const [supplyStatusDialog, setSupplyStatusDialog] = useState(false);
  const [supplyAction, setSupplyAction] = useState(null);
  const [supplyObservation, setSupplyObservation] = useState("");
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [resR, statsR, calR, chartR, supR, supStatsR, blocksR] = await Promise.all([
        axios.get(`${API}/reservations`, authHeaders),
        axios.get(`${API}/reservations/stats/summary`, authHeaders),
        axios.get(`${API}/reservations/calendar/data`, authHeaders),
        axios.get(`${API}/reservations/stats/charts`, authHeaders),
        axios.get(`${API}/supply-requests`, authHeaders),
        axios.get(`${API}/supply-requests/stats/summary`, authHeaders),
        axios.get(`${API}/admin-blocks`, authHeaders)
      ]);
      setReservations(resR.data);
      setStats(statsR.data);
      setCalendarData(calR.data);
      setChartData(chartR.data);
      setSupplyRequests(supR.data);
      setSupplyStats(supStatsR.data);
      setAdminBlocks(blocksR.data);
    } catch (error) {
      if (error.response?.status === 401) { toast.error("Sesión expirada"); onLogout(); }
      else toast.error("Error al cargar datos");
    } finally { setIsLoading(false); }
  }, [token, onLogout]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusUpdate = async () => {
    if (!statusAction) return;
    try {
      const payload = { status: statusAction.status };
      if (observation.trim()) payload.observation = observation.trim();
      await axios.patch(`${API}/reservations/${statusAction.id}`, payload, authHeaders);
      toast.success(`Reservación ${statusAction.status === "approved" ? "aprobada" : "rechazada"}`);
      setStatusDialogOpen(false); setStatusAction(null); setObservation(""); fetchData();
    } catch { toast.error("Error al actualizar"); }
  };

  const handleDelete = async () => {
    if (!reservationToDelete) return;
    try {
      await axios.delete(`${API}/reservations/${reservationToDelete.id}`, authHeaders);
      toast.success("Eliminada"); setDeleteDialogOpen(false); setReservationToDelete(null); fetchData();
    } catch { toast.error("Error"); }
  };

  const handleSupplyStatus = async () => {
    if (!supplyAction) return;
    try {
      const payload = { status: supplyAction.status };
      if (supplyObservation.trim()) payload.observation = supplyObservation.trim();
      await axios.patch(`${API}/supply-requests/${supplyAction.id}`, payload, authHeaders);
      toast.success(`Solicitud ${supplyAction.status === "approved" ? "aprobada" : "rechazada"}`);
      setSupplyStatusDialog(false); setSupplyAction(null); setSupplyObservation(""); fetchData();
    } catch { toast.error("Error"); }
  };

  const handleDeleteSupply = async (id) => {
    try { await axios.delete(`${API}/supply-requests/${id}`, authHeaders); toast.success("Eliminada"); fetchData(); } catch { toast.error("Error"); }
  };

  const handleCreateBlock = async () => {
    if (!blockDate) { toast.error("Selecciona una fecha"); return; }
    if (!blockFullDay && (!blockTimeStart || !blockTimeEnd)) { toast.error("Selecciona horario de inicio y fin"); return; }
    if (!blockFullDay && blockTimeEnd <= blockTimeStart) { toast.error("La hora de fin debe ser después de la hora de inicio"); return; }
    try {
      const payload = {
        date: format(blockDate, "yyyy-MM-dd"),
        time_start: blockFullDay ? "" : blockTimeStart,
        time_end: blockFullDay ? "" : blockTimeEnd,
        salon: blockSalon === "all" ? "" : (blockSalon || ""),
        reason: blockReason
      };
      await axios.post(`${API}/admin-blocks`, payload, authHeaders);
      toast.success("Bloqueo creado");
      setBlockDate(null); setBlockTimeStart(""); setBlockTimeEnd(""); setBlockSalon(""); setBlockReason(""); setBlockFullDay(true);
      fetchData();
    } catch { toast.error("Error al crear bloqueo"); }
  };

  const handleDeleteBlock = async (id) => {
    try { await axios.delete(`${API}/admin-blocks/${id}`, authHeaders); toast.success("Bloqueo eliminado"); fetchData(); } catch { toast.error("Error"); }
  };

  const handleExportCSV = async () => {
    try { const res = await axios.get(`${API}/reservations/export/csv`, { ...authHeaders, responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = 'reservaciones.csv'; document.body.appendChild(a); a.click(); a.remove(); toast.success("CSV descargado"); } catch { toast.error("Error"); }
  };

  const handleExportPDF = () => {
    const statusLabel = (s) => s === 'pending' ? 'Pendiente' : s === 'approved' ? 'Aprobada' : 'Rechazada';
    const getResText = (r) => { if (!r) return 'Ninguno'; const items = []; Object.entries({chairs:'Sillas',tables:'Mesas',projector:'Proyector',tv:'TV',microphone:'Micrófono',water:'Botellas de Agua'}).forEach(([k,l]) => { const v = r[k]; if (typeof v === 'number' && v > 0) items.push(`${l}: ${v}`); else if (typeof v === 'boolean' && v) items.push(l); }); if (r.other_description) items.push(`Otras: ${r.other_description}`); return items.join(', ') || 'Ninguno'; };
    const html = `<html><head><title>Reporte</title><style>*{box-sizing:border-box}body{font-family:Arial,sans-serif;padding:30px;font-size:11px}h1{color:#EA580C;font-size:22px}h2{color:#EA580C;font-size:16px;border-bottom:2px solid #EA580C;padding-bottom:5px}.stats{display:flex;gap:20px;margin:15px 0}.stat-box{border:1px solid #E5E5E5;padding:10px 15px;text-align:center}.stat-box .num{font-size:24px;font-weight:bold}.stat-box .label{font-size:10px;text-transform:uppercase;color:#666}.reservation{border:1px solid #E5E5E5;margin:10px 0;padding:15px;page-break-inside:avoid}.reservation .event-name{font-size:14px;font-weight:bold;color:#EA580C}.grid{display:grid;grid-template-columns:1fr 1fr;gap:5px}.field{margin:3px 0}.field .label{font-weight:bold;color:#333}.observation{background:#FEF3C7;border-left:3px solid #F59E0B;padding:8px;margin-top:8px}.badge{padding:3px 10px;font-size:10px;font-weight:bold;text-transform:uppercase}.badge-pending{background:#FEF3C7;color:#92400E}.badge-approved{background:#D1FAE5;color:#065F46}.badge-rejected{background:#FEE2E2;color:#991B1B}</style></head><body><h1>Casa Ciudadana Zapopan</h1><p style="color:#666;font-size:12px">Reporte generado: ${new Date().toLocaleDateString('es-MX',{year:'numeric',month:'long',day:'numeric'})}</p><div class="stats"><div class="stat-box"><div class="num">${stats.total}</div><div class="label">Total</div></div><div class="stat-box"><div class="num" style="color:#92400E">${stats.pending}</div><div class="label">Pendientes</div></div><div class="stat-box"><div class="num" style="color:#065F46">${stats.approved}</div><div class="label">Aprobadas</div></div><div class="stat-box"><div class="num" style="color:#991B1B">${stats.rejected}</div><div class="label">Rechazadas</div></div></div><h2>Reservaciones (${reservations.length})</h2>${reservations.map(r=>`<div class="reservation"><div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;border-bottom:1px solid #E5E5E5;padding-bottom:8px"><span class="event-name">${r.event_name||'Sin nombre'}</span><span class="badge badge-${r.status}">${statusLabel(r.status)}</span></div><div class="grid"><div class="field"><span class="label">Organizador:</span> ${r.organizer_name}</div><div class="field"><span class="label">Correo:</span> ${r.organizer_email}</div><div class="field"><span class="label">Teléfono:</span> ${r.organizer_phone||'N/A'}</div><div class="field"><span class="label">Fecha:</span> ${r.event_date}</div><div class="field"><span class="label">Horario:</span> ${r.event_time_start||''} - ${r.event_time_end||''}</div><div class="field"><span class="label">Personas:</span> ${r.num_people}</div></div><div class="field" style="margin-top:8px"><span class="label">Recursos:</span> ${getResText(r.resources)}</div>${r.observation?`<div class="observation"><strong>Obs:</strong> ${r.observation}</div>`:''}</div>`).join('')}</body></html>`;
    const w = window.open('','_blank'); w.document.write(html); w.document.close(); w.print();
  };

  const filteredRes = reservations.filter(r => filter === "all" || r.status === filter);
  const filteredSup = supplyRequests.filter(s => supplyFilter === "all" || s.status === supplyFilter);

  const getSelectedResources = (resources) => {
    if (!resources) return [];
    return Object.entries(resources).filter(([k, v]) => k !== "other_description" && k !== "other" && ((typeof v === "boolean" && v) || (typeof v === "number" && v > 0))).map(([k, v]) => ({ key: k, qty: typeof v === "number" ? v : 1 }));
  };

  const supplyPieData = [
    { name: 'Pendiente', value: supplyStats.pending },
    { name: 'Aprobada', value: supplyStats.approved },
    { name: 'Rechazada', value: supplyStats.rejected }
  ];
  const supplyMaterialData = [
    { name: 'Bolos', cantidad: supplyStats.materials?.bolos || 0 },
    { name: 'Juguetes', cantidad: supplyStats.materials?.juguetes || 0 },
    { name: 'Material', cantidad: supplyStats.materials?.despensas || 0 }
  ].filter(d => d.cantidad > 0);

  const FilterButtons = ({ current, onChange }) => (
    <div className="flex gap-2 mb-4">
      {[{ value: "all", label: "Todas" }, { value: "pending", label: "Pendientes" }, { value: "approved", label: "Aprobadas" }, { value: "rejected", label: "Rechazadas" }].map(t => (
        <button key={t.value} onClick={() => onChange(t.value)} className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors ${current === t.value ? "bg-[#EA580C] text-white" : "bg-white text-[#0A0A0A] border border-[#E5E5E5] hover:border-[#EA580C]"}`}>{t.label}</button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E5E5] p-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <p className="label-text mb-1">Panel de Control</p>
            <h1 className="heading-h2">Casa Ciudadana Zapopan</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={handleExportCSV} className="flex items-center gap-1 px-3 py-2 border border-[#E5E5E5] bg-white text-sm font-semibold hover:border-[#EA580C]" data-testid="export-csv-button"><Download className="w-4 h-4" />CSV</button>
            <button onClick={handleExportPDF} className="flex items-center gap-1 px-3 py-2 border border-[#E5E5E5] bg-white text-sm font-semibold hover:border-[#EA580C]" data-testid="export-pdf-button"><FileText className="w-4 h-4" />PDF</button>
            <button onClick={fetchData} className="btn-primary flex items-center gap-2" data-testid="refresh-button"><RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />Actualizar</button>
          </div>
        </div>
      </div>

      {/* Main Tabs: Reservaciones / Insumos */}
      <div className="max-w-7xl mx-auto p-6">
        <Tabs defaultValue="reservations" className="w-full">
          <TabsList className="mb-6 bg-white border border-[#E5E5E5] p-1 rounded-none">
            <TabsTrigger value="reservations" className="rounded-none data-[state=active]:bg-[#EA580C] data-[state=active]:text-white px-6 text-sm" data-testid="main-tab-reservations">
              <CalendarIcon className="w-4 h-4 mr-1 inline" /> Reservaciones ({stats.total})
            </TabsTrigger>
            <TabsTrigger value="supplies" className="rounded-none data-[state=active]:bg-[#EA580C] data-[state=active]:text-white px-6 text-sm" data-testid="main-tab-supplies">
              <Package className="w-4 h-4 mr-1 inline" /> Insumos ({supplyStats.total})
            </TabsTrigger>
          </TabsList>

          {/* ===================== RESERVACIONES ===================== */}
          <TabsContent value="reservations">
            {/* Stats */}
            <div className="admin-grid bg-white border border-[#E5E5E5] mb-6">
              <StatCard label="Total" value={stats.total} color="text-[#0A0A0A]" />
              <StatCard label="Pendientes" value={stats.pending} color="text-yellow-600" />
              <StatCard label="Aprobadas" value={stats.approved} color="text-green-600" />
              <StatCard label="Rechazadas" value={stats.rejected} color="text-red-600" />
            </div>

            <Tabs defaultValue="list" className="w-full">
              <TabsList className="mb-4 bg-white border border-[#E5E5E5] p-1 rounded-none">
                <TabsTrigger value="list" className="rounded-none data-[state=active]:bg-[#EA580C] data-[state=active]:text-white px-4 text-xs">Lista</TabsTrigger>
                <TabsTrigger value="calendar" className="rounded-none data-[state=active]:bg-[#EA580C] data-[state=active]:text-white px-4 text-xs">Calendario</TabsTrigger>
                <TabsTrigger value="charts" className="rounded-none data-[state=active]:bg-[#EA580C] data-[state=active]:text-white px-4 text-xs"><BarChart3 className="w-3 h-3 mr-1 inline" />Gráficas</TabsTrigger>
                <TabsTrigger value="blocks" className="rounded-none data-[state=active]:bg-[#EA580C] data-[state=active]:text-white px-4 text-xs" data-testid="tab-blocks"><Ban className="w-3 h-3 mr-1 inline" />Bloqueos</TabsTrigger>
              </TabsList>

              <TabsContent value="list">
                <FilterButtons current={filter} onChange={setFilter} />
                <div className="bg-white border border-[#E5E5E5] overflow-hidden">
                  {isLoading ? <div className="flex justify-center p-16"><Loader2 className="w-8 h-8 animate-spin text-[#EA580C]" /></div>
                  : filteredRes.length === 0 ? <div className="flex flex-col items-center p-16 text-[#666]"><Inbox className="w-12 h-12 mb-4" /><p>No hay reservaciones</p></div>
                  : <div className="overflow-x-auto"><table className="table-swiss" data-testid="reservations-table"><thead><tr><th>Organizador</th><th>Fecha / Hora</th><th>Personas</th><th>Recursos</th><th>Estado</th><th>Acciones</th></tr></thead><tbody>
                    {filteredRes.map(r => (
                      <tr key={r.id} className="fade-in">
                        <td><p className="font-semibold">{r.organizer_name}</p><p className="text-xs text-[#666]">{r.organizer_email}</p>{r.organizer_phone && <p className="text-xs text-[#666]">{r.organizer_phone}</p>}{r.event_name && <p className="text-xs text-[#EA580C] font-medium mt-1">{r.event_name}</p>}{r.event_description && <p className="text-xs text-[#999] italic mt-1 max-w-[200px]">{r.event_description}</p>}</td>
                        <td><span className="flex items-center gap-1 text-sm"><CalendarIcon className="w-4 h-4 text-[#EA580C]" />{format(parseISO(r.event_date), "d MMM yyyy", { locale: es })}</span><span className="flex items-center gap-1 text-sm text-[#666]"><Clock className="w-4 h-4" />{r.event_time_start || r.event_time} - {r.event_time_end || ''}</span></td>
                        <td><span className="flex items-center gap-1"><Users className="w-4 h-4 text-[#EA580C]" />{r.num_people}</span></td>
                        <td><div className="flex flex-wrap gap-1">{getSelectedResources(r.resources).map(({key,qty}) => { const I = RESOURCE_ICONS[key]; return <span key={key} className="flex items-center gap-1 bg-[#F5F5F5] px-2 py-1 text-xs"><I className="w-3 h-3" />{RESOURCE_LABELS[key]}{qty>1?` (${qty})`:''}</span>; })}{r.resources?.other_description && <span className="text-xs text-[#666] italic">({r.resources.other_description})</span>}</div></td>
                        <td><StatusBadge status={r.status} />{r.observation && <p className="text-xs text-[#666] italic mt-1 max-w-[150px]">{r.observation}</p>}</td>
                        <td><div className="flex items-center gap-1">
                          {r.status === "pending" && <><button onClick={() => { setStatusAction({ id: r.id, status: "approved", name: r.organizer_name }); setObservation(""); setStatusDialogOpen(true); }} className="p-2 text-green-600 hover:bg-green-50"><CheckCircle className="w-5 h-5" /></button><button onClick={() => { setStatusAction({ id: r.id, status: "rejected", name: r.organizer_name }); setObservation(""); setStatusDialogOpen(true); }} className="p-2 text-red-600 hover:bg-red-50"><XCircle className="w-5 h-5" /></button></>}
                          <button onClick={() => { setReservationToDelete(r); setDeleteDialogOpen(true); }} className="p-2 text-[#666] hover:bg-[#F5F5F5]"><Trash2 className="w-5 h-5" /></button>
                        </div></td>
                      </tr>
                    ))}
                  </tbody></table></div>}
                </div>
              </TabsContent>

              <TabsContent value="calendar">
                {isLoading ? <div className="flex justify-center p-16 bg-white border border-[#E5E5E5]"><Loader2 className="w-8 h-8 animate-spin text-[#EA580C]" /></div>
                : <CalendarView calendarData={calendarData} onDateClick={(d, e) => { setSelectedDate(d); setSelectedEvents(e); setDayModalOpen(true); }} />}
              </TabsContent>

              <TabsContent value="charts">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border border-[#E5E5E5] p-6">
                      <h3 className="heading-h3 mb-4">Por Estado</h3>
                      <ResponsiveContainer width="100%" height={250}><PieChart><Pie data={chartData.by_status} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" nameKey="name" label={({name,value})=>`${name}: ${value}`}>{chartData.by_status.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}</Pie><Tooltip /><Legend /></PieChart></ResponsiveContainer>
                    </div>
                    <div className="bg-white border border-[#E5E5E5] p-6">
                      <h3 className="heading-h3 mb-4">Por Mes</h3>
                      {chartData.by_month.length > 0 ? <ResponsiveContainer width="100%" height={250}><BarChart data={chartData.by_month}><CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" /><XAxis dataKey="month" tick={{fontSize:12}} /><YAxis allowDecimals={false} tick={{fontSize:12}} /><Tooltip /><Bar dataKey="reservaciones" fill="#EA580C" /></BarChart></ResponsiveContainer> : <p className="text-sm text-[#666] text-center py-16">Sin datos</p>}
                    </div>
                  </div>
                  <div className="bg-white border border-[#E5E5E5] p-6">
                    <h3 className="heading-h3 mb-4">Recursos Solicitados</h3>
                    {chartData.by_resource.length > 0 ? <ResponsiveContainer width="100%" height={280}><BarChart data={chartData.by_resource} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" /><XAxis type="number" allowDecimals={false} tick={{fontSize:12}} /><YAxis type="category" dataKey="name" width={120} tick={{fontSize:12}} /><Tooltip /><Bar dataKey="cantidad" fill="#EA580C" /></BarChart></ResponsiveContainer> : <p className="text-sm text-[#666] text-center py-16">Sin datos</p>}
                  </div>
                  <div className="bg-white border border-[#E5E5E5] p-6 text-center">
                    <p className="label-text mb-2">Total Personas Esperadas</p>
                    <p className="text-5xl font-bold text-[#EA580C]">{chartData.total_people}</p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="blocks">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Create Block Form */}
                  <div className="bg-white border border-[#E5E5E5] p-6" data-testid="create-block-form">
                    <h3 className="heading-h3 mb-4 flex items-center gap-2"><Ban className="w-4 h-4 text-[#EA580C]" /> Nuevo Bloqueo</h3>
                    <div className="space-y-4">
                      <div>
                        <Label className="label-text mb-2 block">Fecha *</Label>
                        <div className="border border-[#E5E5E5] inline-block">
                          <Calendar
                            mode="single"
                            selected={blockDate}
                            onSelect={setBlockDate}
                            locale={es}
                            className="rounded-none"
                            data-testid="block-calendar"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={blockFullDay} onChange={e => { setBlockFullDay(e.target.checked); if (e.target.checked) { setBlockTimeStart(""); setBlockTimeEnd(""); } }} className="w-4 h-4 accent-[#EA580C]" data-testid="block-full-day" />
                          <span className="text-sm font-semibold">Bloquear día completo</span>
                        </label>
                      </div>
                      <div className={`grid grid-cols-2 gap-3 ${blockFullDay ? "opacity-40 pointer-events-none" : ""}`}>
                        <div>
                          <Label className="label-text mb-1 block text-xs">Hora inicio *</Label>
                          <Select value={blockTimeStart} onValueChange={setBlockTimeStart}>
                            <SelectTrigger className="rounded-none text-sm h-9" data-testid="block-time-start"><SelectValue placeholder="Inicio" /></SelectTrigger>
                            <SelectContent>
                              {["08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00","21:00"].map(t => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="label-text mb-1 block text-xs">Hora fin *</Label>
                          <Select value={blockTimeEnd} onValueChange={setBlockTimeEnd}>
                            <SelectTrigger className="rounded-none text-sm h-9" data-testid="block-time-end"><SelectValue placeholder="Fin" /></SelectTrigger>
                            <SelectContent>
                              {["09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00","21:00","22:00"].map(t => (
                                <SelectItem key={t} value={t} disabled={blockTimeStart && t <= blockTimeStart}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label className="label-text mb-1 block text-xs">Salón (opcional, vacío = todos)</Label>
                        <Select value={blockSalon} onValueChange={setBlockSalon}>
                          <SelectTrigger className="rounded-none text-sm h-9" data-testid="block-salon"><SelectValue placeholder="Todos los salones" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos los salones</SelectItem>
                            <SelectItem value="gran_salon">Gran Salón</SelectItem>
                            <SelectItem value="salon_dialogo">Salón de Diálogo</SelectItem>
                            <SelectItem value="salon_privado">Salón Privado</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="label-text mb-1 block text-xs">Motivo (opcional)</Label>
                        <Input
                          value={blockReason}
                          onChange={e => setBlockReason(e.target.value)}
                          placeholder="Ej. Mantenimiento, Evento privado..."
                          className="rounded-none text-sm"
                          data-testid="block-reason"
                        />
                      </div>
                      <button onClick={handleCreateBlock} className="w-full bg-[#EA580C] text-white py-3 font-bold text-sm uppercase tracking-wide hover:bg-orange-700 transition-colors flex items-center justify-center gap-2" data-testid="create-block-btn">
                        <Plus className="w-4 h-4" /> Crear Bloqueo
                      </button>
                    </div>
                  </div>

                  {/* Existing Blocks List */}
                  <div className="lg:col-span-2 bg-white border border-[#E5E5E5]" data-testid="blocks-list">
                    <div className="p-4 border-b border-[#E5E5E5]">
                      <h3 className="heading-h3">Bloqueos Activos ({adminBlocks.length})</h3>
                    </div>
                    {adminBlocks.length === 0 ? (
                      <div className="flex flex-col items-center p-16 text-[#666]">
                        <Ban className="w-12 h-12 mb-4" />
                        <p>No hay bloqueos configurados</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="table-swiss" data-testid="blocks-table">
                          <thead>
                            <tr><th>Fecha</th><th>Horario</th><th>Salón</th><th>Motivo</th><th>Acciones</th></tr>
                          </thead>
                          <tbody>
                            {adminBlocks.map(b => (
                              <tr key={b.id} className="fade-in">
                                <td className="font-semibold">{b.date}</td>
                                <td>{b.time_start ? `${b.time_start} - ${b.time_end}` : <span className="text-red-600 font-semibold">Día completo</span>}</td>
                                <td>{b.salon ? ({gran_salon:"Gran Salón",salon_dialogo:"Salón de Diálogo",salon_privado:"Salón Privado"}[b.salon] || b.salon) : "Todos"}</td>
                                <td className="text-sm text-[#666]">{b.reason || "—"}</td>
                                <td>
                                  <button onClick={() => handleDeleteBlock(b.id)} className="p-2 text-red-600 hover:bg-red-50" data-testid={`delete-block-${b.id}`}>
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* ===================== INSUMOS ===================== */}
          <TabsContent value="supplies">
            {/* Stats */}
            <div className="admin-grid bg-white border border-[#E5E5E5] mb-6">
              <StatCard label="Total" value={supplyStats.total} color="text-[#0A0A0A]" />
              <StatCard label="Pendientes" value={supplyStats.pending} color="text-yellow-600" />
              <StatCard label="Aprobadas" value={supplyStats.approved} color="text-green-600" />
              <StatCard label="Rechazadas" value={supplyStats.rejected} color="text-red-600" />
            </div>

            <Tabs defaultValue="list" className="w-full">
              <TabsList className="mb-4 bg-white border border-[#E5E5E5] p-1 rounded-none">
                <TabsTrigger value="list" className="rounded-none data-[state=active]:bg-[#EA580C] data-[state=active]:text-white px-4 text-xs">Lista</TabsTrigger>
                <TabsTrigger value="charts" className="rounded-none data-[state=active]:bg-[#EA580C] data-[state=active]:text-white px-4 text-xs"><BarChart3 className="w-3 h-3 mr-1 inline" />Gráficas</TabsTrigger>
              </TabsList>

              <TabsContent value="list">
                <FilterButtons current={supplyFilter} onChange={setSupplyFilter} />
                <div className="bg-white border border-[#E5E5E5] overflow-hidden">
                  {isLoading ? <div className="flex justify-center p-16"><Loader2 className="w-8 h-8 animate-spin text-[#EA580C]" /></div>
                  : filteredSup.length === 0 ? <div className="flex flex-col items-center p-16 text-[#666]"><Inbox className="w-12 h-12 mb-4" /><p>No hay solicitudes</p></div>
                  : <div className="overflow-x-auto"><table className="table-swiss" data-testid="supplies-table"><thead><tr><th>Referente</th><th>Persona que Recoge</th><th>Fecha / Hora</th><th>Material</th><th>Estado</th><th>Acciones</th></tr></thead><tbody>
                    {filteredSup.map(s => (
                      <tr key={s.id} className="fade-in">
                        <td><p className="font-semibold">{s.referent_name}</p><p className="text-xs text-[#666]">{s.email}</p></td>
                        <td><p className="font-semibold">{s.collector_name}</p><p className="text-xs text-[#666]">{s.collector_phone}</p></td>
                        <td>{s.pickup_date && <span className="flex items-center gap-1 text-sm"><CalendarIcon className="w-4 h-4 text-[#EA580C]" />{s.pickup_date}</span>}{s.pickup_time && <span className="flex items-center gap-1 text-sm text-[#666]"><Clock className="w-4 h-4" />{s.pickup_time} hrs</span>}</td>
                        <td><div className="flex flex-wrap gap-1">{s.materials?.bolos > 0 && <span className="bg-[#F5F5F5] px-2 py-1 text-xs">Bolos: {s.materials.bolos}</span>}{s.materials?.juguetes > 0 && <span className="bg-[#F5F5F5] px-2 py-1 text-xs">Juguetes: {s.materials.juguetes}</span>}{s.materials?.despensas > 0 && <span className="bg-[#F5F5F5] px-2 py-1 text-xs">Material: {s.materials.despensas}</span>}</div></td>
                        <td><StatusBadge status={s.status} />{s.observation && <p className="text-xs text-[#666] italic mt-1">{s.observation}</p>}</td>
                        <td><div className="flex items-center gap-1">
                          {s.status === "pending" && <><button onClick={() => { setSupplyAction({ id: s.id, status: "approved", name: s.referent_name }); setSupplyObservation(""); setSupplyStatusDialog(true); }} className="p-2 text-green-600 hover:bg-green-50"><CheckCircle className="w-5 h-5" /></button><button onClick={() => { setSupplyAction({ id: s.id, status: "rejected", name: s.referent_name }); setSupplyObservation(""); setSupplyStatusDialog(true); }} className="p-2 text-red-600 hover:bg-red-50"><XCircle className="w-5 h-5" /></button></>}
                          <button onClick={() => handleDeleteSupply(s.id)} className="p-2 text-[#666] hover:bg-[#F5F5F5]"><Trash2 className="w-5 h-5" /></button>
                        </div></td>
                      </tr>
                    ))}
                  </tbody></table></div>}
                </div>
              </TabsContent>

              <TabsContent value="charts">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border border-[#E5E5E5] p-6">
                      <h3 className="heading-h3 mb-4">Solicitudes por Estado</h3>
                      <ResponsiveContainer width="100%" height={250}><PieChart><Pie data={supplyPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" nameKey="name" label={({name,value})=>`${name}: ${value}`}>{supplyPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}</Pie><Tooltip /><Legend /></PieChart></ResponsiveContainer>
                    </div>
                    <div className="bg-white border border-[#E5E5E5] p-6">
                      <h3 className="heading-h3 mb-4">Material Solicitado (Total)</h3>
                      {supplyMaterialData.length > 0 ? <ResponsiveContainer width="100%" height={250}><BarChart data={supplyMaterialData}><CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" /><XAxis dataKey="name" tick={{fontSize:12}} /><YAxis allowDecimals={false} tick={{fontSize:12}} /><Tooltip /><Bar dataKey="cantidad" fill="#EA580C" /></BarChart></ResponsiveContainer> : <p className="text-sm text-[#666] text-center py-16">Sin datos</p>}
                    </div>
                  </div>
                  <div className="bg-white border border-[#E5E5E5] p-6 text-center">
                    <p className="label-text mb-2">Total de Insumos Solicitados</p>
                    <p className="text-5xl font-bold text-[#EA580C]">{(supplyStats.materials?.bolos || 0) + (supplyStats.materials?.juguetes || 0) + (supplyStats.materials?.despensas || 0)}</p>
                    <div className="flex justify-center gap-6 mt-3 text-sm text-[#666]">
                      <span>Bolos: {supplyStats.materials?.bolos || 0}</span>
                      <span>Juguetes: {supplyStats.materials?.juguetes || 0}</span>
                      <span>Material: {supplyStats.materials?.despensas || 0}</span>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialogs */}
      <AlertDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader><AlertDialogTitle>{statusAction?.status === "approved" ? "Aprobar" : "Rechazar"} reservación</AlertDialogTitle><AlertDialogDescription>Reservación de {statusAction?.name}. Se notificará por email.</AlertDialogDescription></AlertDialogHeader>
          <div className="px-6 pb-2"><label className="label-text block mb-2">Observaciones (opcional)</label><Textarea value={observation} onChange={e => setObservation(e.target.value)} placeholder="Escribe una observación..." className="rounded-none min-h-[80px] resize-none" data-testid="observation-input" /></div>
          <AlertDialogFooter><AlertDialogCancel className="rounded-none">Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleStatusUpdate} className={`rounded-none ${statusAction?.status === "approved" ? "bg-green-600 hover:bg-green-700" : "bg-[#E31837] hover:bg-red-700"}`}>{statusAction?.status === "approved" ? "Aprobar" : "Rechazar"}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar reservación?</AlertDialogTitle><AlertDialogDescription>Se eliminará permanentemente la reservación de {reservationToDelete?.organizer_name}.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-none">Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="rounded-none bg-[#E31837] hover:bg-red-700">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={supplyStatusDialog} onOpenChange={setSupplyStatusDialog}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader><AlertDialogTitle>{supplyAction?.status === "approved" ? "Aprobar" : "Rechazar"} solicitud de insumos</AlertDialogTitle><AlertDialogDescription>Solicitud de {supplyAction?.name}. Se notificará por email.</AlertDialogDescription></AlertDialogHeader>
          <div className="px-6 pb-2"><label className="label-text block mb-2">Observaciones (opcional)</label><Textarea value={supplyObservation} onChange={e => setSupplyObservation(e.target.value)} placeholder="Escribe una observación..." className="rounded-none min-h-[80px] resize-none" /></div>
          <AlertDialogFooter><AlertDialogCancel className="rounded-none">Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleSupplyStatus} className={`rounded-none ${supplyAction?.status === "approved" ? "bg-green-600 hover:bg-green-700" : "bg-[#E31837] hover:bg-red-700"}`}>{supplyAction?.status === "approved" ? "Aprobar" : "Rechazar"}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DayDetailModal isOpen={dayModalOpen} onClose={() => setDayModalOpen(false)} date={selectedDate} events={selectedEvents} />
    </div>
  );
};

export default AdminPage;
